import {
  ceilFee,
  floorFee,
  BPS_DENOMINATOR as MARKET_MATH_BPS_DENOMINATOR,
} from '@morfi/market-math';
import BN from 'bn.js';

/**
 * Convert a human-readable USDC amount to D6 (on-chain scale).
 *
 * Formula: result = round(usdc * 1_000_000)
 *
 * @param usdc - Human-readable USDC amount (e.g. 1.5 = $1.50)
 * @returns BN in D6 scale (e.g. 1_500_000)
 */
export function usdcToD6(usdc: number): BN {
  return new BN(Math.round(usdc * 1_000_000));
}

/**
 * Convert a D6 on-chain amount to human-readable USDC.
 *
 * Formula: result = d6 / 1_000_000
 *
 * @param d6 - On-chain amount in D6 scale (e.g. 1_500_000)
 * @returns Human-readable USDC (e.g. 1.5)
 */
export function d6ToUsdc(d6: BN): number {
  return d6.toNumber() / 1_000_000;
}

/**
 * Convert a human-readable value to D9 scale (used for prices and unitless proportions).
 *
 * Formula: result = round(value * 1_000_000_000)
 *
 * @param value - Human-readable value (e.g. 0.5 for $0.50 per token)
 * @returns BN in D9 scale (e.g. 500_000_000)
 */
export function toD9(value: number): BN {
  return new BN(Math.round(value * 1_000_000_000));
}

/**
 * Convert a D9 on-chain value to a human-readable number.
 *
 * Formula: result = d9 / 1_000_000_000
 *
 * @param d9 - On-chain value in D9 scale (e.g. 500_000_000)
 * @returns Human-readable number (e.g. 0.5)
 */
export function fromD9(d9: BN): number {
  return d9.toNumber() / 1_000_000_000;
}

/**
 * Convert basis points to a decimal fraction.
 *
 * Formula: result = bps / 10_000
 *
 * @param bps - Value in basis points (e.g. 50 bps)
 * @returns Decimal fraction (e.g. 0.005)
 */
export function bpsToFraction(bps: number): number {
  return bps / 10_000;
}

/**
 * Apply a fee in basis points with ceiling rounding (protocol-favorable).
 *
 * Formula: fee = ceil(amount * feeBps / 10000), net = amount - fee
 *
 * @param amount - Input amount (D6)
 * @param feeBps - Fee rate in basis points (e.g. 100 = 1%)
 * @returns Object with fee (D6, ceil-rounded) and net (D6, amount minus fee)
 */
export function applyFeeBps(amount: BN, feeBps: number): { fee: BN; net: BN } {
  const fee = bnFromBigint(
    ceilFee(BigInt(amount.toString(10)), BigInt(feeBps), MARKET_MATH_BPS_DENOMINATOR),
  );
  const net = amount.sub(fee);
  return { fee, net };
}

/**
 * Apply slippage tolerance to an amount.
 *
 * - 'floor': min output = floor(amount * (10000 - slippageBps) / 10000)
 * - 'ceil':  max input  = ceil(amount * (10000 + slippageBps) / 10000)
 *
 * @param amount - Base amount (D6)
 * @param slippageBps - Slippage tolerance in basis points (e.g. 50 = 0.5%)
 * @param direction - 'floor' for minimum output, 'ceil' for maximum input
 * @returns Slippage-adjusted amount (D6)
 */
export function applySlippage(amount: BN, slippageBps: number, direction: 'floor' | 'ceil'): BN {
  const amountBigint = BigInt(amount.toString(10));
  const slippage = BigInt(slippageBps);
  if (direction === 'floor') {
    return bnFromBigint(
      floorFee(amountBigint, MARKET_MATH_BPS_DENOMINATOR - slippage, MARKET_MATH_BPS_DENOMINATOR),
    );
  }
  return bnFromBigint(
    ceilFee(amountBigint, MARKET_MATH_BPS_DENOMINATOR + slippage, MARKET_MATH_BPS_DENOMINATOR),
  );
}

function bnFromBigint(value: bigint): BN {
  return new BN(value.toString());
}
